import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";
import MenuButton from "./MenuButton";
import ThemeToggle from "./ThemeToggle";
import Button from "@/components/base/Button";
import { useScrolled } from "@/hooks/useUi";
import { useTheme } from "@/context/ThemeContext";

const leftItems = [
  { label: "Inicio", to: "/" },
  { label: "Servicios", to: "/servicios" },
  { label: "Flota", to: "/flota" },
];

const rightItems = [
  { label: "Nosotros", to: "/nosotros" },
  { label: "Trabajos", to: "/trabajos" },
  { label: "Contacto", to: "/contacto" },
];

const mobileItems = [
  { label: "Inicio", to: "/" },
  { label: "Servicios", to: "/servicios" },
  { label: "Flota", to: "/flota" },
  { label: "Nosotros", to: "/nosotros" },
  { label: "Trabajos", to: "/trabajos" },
  { label: "Contacto", to: "/contacto" },
];

export default function Navbar() {
  const scrolled = useScrolled(32);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { resolvedTheme } = useTheme();

  // En modo oscuro el header es siempre sólido (gris), por eso no se vuelve transparente.
  const isDark = resolvedTheme === "dark";
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen && !isDark;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const headerClass = transparent
    ? "bg-transparent"
    : "border-b border-background-200 bg-background-50/90 backdrop-blur-md dark:border-[#7f7e7f] dark:bg-[#959394]";

  const linkClass = (isActive: boolean) =>
    `rounded-md px-3 py-2 text-sm font-label font-medium whitespace-nowrap transition-colors duration-200 ${
      transparent
        ? isActive
          ? "text-background-50"
          : "text-background-100/80 hover:text-background-50"
        : isActive
          ? "bg-primary-100 text-primary-800 dark:bg-white dark:text-primary-800"
          : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950 dark:text-foreground-50 dark:hover:bg-black/10 dark:hover:text-black"
    }`;

  return (
    <header className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${headerClass}`}>
      {/* Navegación de escritorio: izquierda | LOGO centrado | derecha */}
      <div
        className={`hidden w-full grid-cols-[1fr_auto_1fr] items-center gap-4 px-8 transition-all duration-300 lg:grid ${
          scrolled ? "h-20" : "h-24"
        }`}
      >
        <nav className="flex items-center justify-end gap-1" aria-label="Navegación principal">
          {leftItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => linkClass(isActive)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center justify-center">
          <Logo heightClass={scrolled ? "h-16" : "h-20"} transition light={transparent} />
        </div>

        <div className="flex items-center gap-2">
          <nav className="flex items-center gap-1" aria-label="Navegación secundaria">
            {rightItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => linkClass(isActive)}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle light={transparent} />
            <Button
            to="/solicitar-servicio"
            variant="primary"
            size="md"
              icon="ri-send-plane-line"
              className="ml-1 hidden xl:inline-flex"
            >
              Solicitar servicio
            </Button>
          </div>
        </div>
      </div>

      {/* Barra móvil: logo centrado + botón escalonado a la derecha */}
      <div className="grid h-20 w-full grid-cols-[1fr_auto_1fr] items-center px-4 lg:hidden">
        <span aria-hidden="true" />
        <Logo heightClass="h-14" light={transparent} />
        <div className="flex justify-end">
          <MenuButton
            open={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            light={transparent}
          />
        </div>
      </div>

      {/* Panel de menú móvil */}
      {menuOpen && (
        <div className="animate-menu-drop border-t border-background-200 bg-background-50 lg:hidden">
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto px-4 pb-8 pt-4">
            <nav className="flex flex-col gap-1" aria-label="Navegación móvil">
              {mobileItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-lg px-4 py-3 text-base font-label font-medium transition-colors duration-200 ${
                      isActive ? "bg-primary-100 text-primary-800" : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400" aria-hidden="true"></i>
                </NavLink>
              ))}
            </nav>

            <div className="mt-5 flex flex-col gap-3">
              <Button to="/solicitar-servicio" variant="primary" size="lg" icon="ri-send-plane-line" fullWidth>
                Solicitar servicio
              </Button>
              <div className="flex items-center justify-between rounded-lg border border-background-200 px-4 py-2.5">
                <span className="text-sm font-label text-foreground-600">Apariencia</span>
                <ThemeToggle />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}